/** FSDS platform default — matches `:root { --primary: 4 74% 49% }` in globals.css */
export const FSDS_DEFAULT_PRIMARY_HEX = '#D92D20'

/** Community default when unset — matches `.theme-non-fsds { --primary: 221 83% 53% }` */
export const COMMUNITY_DEFAULT_PRIMARY_HEX = '#3B82F6'

/** Normalize to #RRGGBB or null when invalid / empty. */
export function normalizeHexColor(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  if (!trimmed) {
    return null
  }
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  if (!/^#[0-9a-fA-F]{6}$/.test(withHash)) {
    return null
  }
  return withHash.toUpperCase()
}

/** Tailwind/shadcn HSL components: "221 83% 53%" (no hsl() wrapper). */
export function hexToHslComponents(hex: string): string {
  const normalized = normalizeHexColor(hex)
  if (!normalized) {
    return '221 83% 53%'
  }

  const r = parseInt(normalized.slice(1, 3), 16) / 255
  const g = parseInt(normalized.slice(3, 5), 16) / 255
  const b = parseInt(normalized.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    switch (max) {
      case r:
        h = ((g - b) / delta + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / delta + 2) / 6
        break
      default:
        h = ((r - g) / delta + 4) / 6
        break
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

function relativeLuminance(hex: string): number {
  const normalized = normalizeHexColor(hex)
  if (!normalized) {
    return 0
  }

  const channels = [1, 3, 5].map(start => {
    const value = parseInt(normalized.slice(start, start + 2), 16) / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

export function getPrimaryForegroundHsl(hex: string): string {
  return relativeLuminance(hex) > 0.45 ? '222 47% 11%' : '0 0% 100%'
}

const TENANT_PRIMARY_CSS_VARS = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--primary-color',
] as const

/** Apply community primary color to document CSS variables used by shadcn/Tailwind. */
export function applyTenantPrimaryTheme(root: HTMLElement, primaryHex: string | null | undefined): void {
  const normalized = normalizeHexColor(primaryHex)
  if (!normalized) {
    TENANT_PRIMARY_CSS_VARS.forEach(name => root.style.removeProperty(name))
    return
  }

  const primary = hexToHslComponents(normalized)
  root.style.setProperty('--primary', primary)
  root.style.setProperty('--ring', primary)
  root.style.setProperty('--primary-foreground', getPrimaryForegroundHsl(normalized))
  root.style.setProperty('--primary-color', normalized)
}

export const INVALID_PRIMARY_COLOR_MESSAGE = 'Primary color must be a valid hex code (e.g. #2563EB).'

export function validatePrimaryColor(value: string): { ok: true; value: string | null } | { ok: false; message: string } {
  const trimmed = value.trim()
  if (!trimmed) {
    return { ok: true, value: null }
  }
  const normalized = normalizeHexColor(trimmed)
  if (!normalized) {
    return { ok: false, message: INVALID_PRIMARY_COLOR_MESSAGE }
  }
  return { ok: true, value: normalized }
}
